import React, { useState, useRef, useEffect, memo } from 'react';
import apiRequest from "../../services/apiClient";
import { isSuccessStatusCode } from "../../utils/utils";
import { useSnackbar } from 'notistack';
import { invalidateCache } from '../../components/serverAutocomplete.component';

// Singleton para manejar una sola instancia de ClientForm
class ClientFormManager {
  constructor() {
    this.currentRequest = null;
    this.isLoading = false;
    this.listeners = new Set();
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  notify(data) {
    this.listeners.forEach(listener => listener(data));
  }

  async loadClient(editingItem) {
    if (this.isLoading) {
      console.log('Already loading form data, ignoring request for:', editingItem);
      return;
    }

    console.log('Making API request for client form data:', editingItem);
    this.isLoading = true;
    
    // Cancelar petición anterior si existe
    if (this.currentRequest) {
      console.log('Cancelling previous form request');
      this.currentRequest.cancel?.();
    }
    
    const request = apiRequest(`client/${editingItem}`);
    this.currentRequest = request;
    
    try {
      const response = await request;
      console.log('API response received for client form:', editingItem, response.data);
      this.notify({ type: 'SUCCESS', data: response.data });
    } catch (error) {
      console.error('Error loading client form:', error);
      this.notify({ type: 'ERROR', error });
    } finally {
      this.isLoading = false;
      this.currentRequest = null;
    }
  }

  clear() {
    this.isLoading = false;
    if (this.currentRequest) {
      this.currentRequest.cancel?.();
      this.currentRequest = null;
    }
    this.notify({ type: 'CLEAR' });
  }
}

const clientFormManager = new ClientFormManager();

const ClientFormSingleton = memo(({ editingItem, closeForm, onClientAdd }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [item, setItem] = useState({});

  useEffect(() => {
    const unsubscribe = clientFormManager.subscribe(({ type, data, error }) => {
      switch (type) {
        case 'SUCCESS':
          setItem(data);
          setLoading(false);
          break;
        case 'ERROR':
          setLoading(false);
          break;
        case 'CLEAR':
          setItem({});
          setLoading(false);
          break;
      }
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (editingItem) {
      setLoading(true);
      clientFormManager.loadClient(editingItem);
    } else {
      clientFormManager.clear();
    }
  }, [editingItem]);
  
  const handleSubmit = (event) => {
    event.preventDefault();

    setSubmitting(true);
    apiRequest("client", {
      method: !editingItem ? "POST" : "PUT",
      data: item
    })
    .then(response => {
      if (isSuccessStatusCode(response.status)) {
        onClientAdd();
        // Invalidar caché de clientes para refrescar las listas
        invalidateCache('client');
      }
      setSubmitting(false);
    })
    .catch(error => {
      enqueueSnackbar(error.response?.data?.message || "An error occurred", {
        variant: 'error',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
        autoHideDuration: 3000
      });
      try{
        var validationErrors = error.response.data.validationErrors;
        if(validationErrors instanceof Array){
          for (let i = 0; i < validationErrors.length; i++) {
            const element = validationErrors[i];
            let errorMessages = "";
            element.errorMessages.forEach(em => {
              errorMessages += em;
            });
            setErrors({ ...errors, [element.field]: errorMessages })
          }
        }
      } catch(e){
        // 
      }
      setSubmitting(false);
    });
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                name="name"
                value={item?.name || ''}
                onChange={(e) => {
                  setItem({ ...item, [e.target.name]: e.target.value })
                }}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors["name"] ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {errors["name"] && (
                <span className="text-red-500 text-sm">{errors["name"]}</span>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Identity Number</label>
              <input
                type="text"
                name="identityNumber"
                value={item?.identityNumber || ''}
                onChange={(e) => {
                  setItem({ ...item, [e.target.name]: e.target.value })
                }}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors["identityNumber"] ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {errors["identityNumber"] && (
                <span className="text-red-500 text-sm">{errors["identityNumber"]}</span>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Fixed Actions */}
      <div className="p-6 pt-4 border-t border-gray-200 bg-white">
        <div className="flex space-x-3">
          <button
            onClick={closeForm}
            className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors disabled:opacity-50"
          >
            {submitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
});

ClientFormSingleton.displayName = 'ClientFormSingleton';

export default ClientFormSingleton;
